/**
 * Emergent Google Auth callback page.
 *
 * Users land here at /auth-callback#session_id=<sid> after the Emergent OAuth
 * hand-off. We POST the session_id to our backend which exchanges it with the
 * Emergent provider and returns our own JWT + user (drop-in compatible with
 * the useAuth() context — everything else in the app keeps working unchanged).
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ShieldCheck, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [error, setError] = useState(null);
  const processed = useRef(false);

  useEffect(() => {
    // useRef guard for React StrictMode's double-invocation — the session_id
    // is single-use, so we must not exchange it twice.
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    if (!sessionId) {
      setError('Missing session token — please try signing in again.');
      return;
    }

    (async () => {
      try {
        const r = await api.post('/auth/google-session', { session_id: sessionId });
        const { token, user } = r.data || {};
        if (!token || !user) throw new Error('Invalid session response');
        setSession(token, user);
        // Wipe the URL fragment so a page refresh doesn't retry the exchange.
        window.history.replaceState(null, '', '/');
        toast.success(`Welcome, ${user.name || user.email}`);
        navigate('/account', { replace: true });
      } catch (err) {
        console.error('Google auth exchange failed:', err);
        setError(err.response?.data?.detail || 'Sign-in failed. Please try again.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-md mx-auto px-4 py-24 text-center">
      {error ? (
        <>
          <AlertCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-black text-[#12263A] mb-2">Sign-in failed</h1>
          <p className="text-neutral-600 mb-6" data-testid="auth-callback-error">{error}</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            data-testid="auth-callback-back-btn"
            className="bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold px-6 py-3 rounded-sm"
          >
            Back to sign in
          </button>
        </>
      ) : (
        <>
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-orange-50 flex items-center justify-center">
            <ShieldCheck className="w-7 h-7 text-[#F46A25]" />
          </div>
          <h1 className="text-2xl font-black text-[#12263A] mb-2">Signing you in…</h1>
          <p className="text-neutral-600 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Verifying your Google session
          </p>
        </>
      )}
    </div>
  );
};

export default AuthCallback;
